export interface ProviderFieldDef {
  key: string;
  label: string;
  type: 'text' | 'password' | 'email' | 'textarea';
  hint?: string;
}

export type ProviderTag = 'sales' | 'marketing' | 'finance' | 'content' | 'cs';

export interface ProviderDef {
  name: string;
  description: string;
  category: string;
  fields: ProviderFieldDef[];
  hidden?: boolean;
  /**
   * If true, multiple integration rows can be created for this provider
   * (e.g. one Calendly per sales rep). The Integrations page keeps the
   * provider in the Available list even after one row exists.
   */
  multiInstance?: boolean;
  /** Which team(s) rely on this integration — drives the badges and the Usage view on the Integrations page. */
  tags?: readonly ProviderTag[];
  /** One-liner on how & where the OS uses this integration, shown in the Usage view. */
  usage?: string;
}

const _PROVIDERS = {
  attio: {
    name: 'Attio',
    description: 'CRM for companies, contacts, and deals',
    category: 'CRM',
    tags: ['sales'],
    usage: 'CRM source of truth — feeds the Sales tab (pipeline, quarters, trends, ABM lists), SDR bookings, and closed-won Slack alerts.',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
      { key: 'webhookSecret', label: 'Webhook Secret (optional)', type: 'password' },
      { key: 'sourceFieldSlug', label: 'Lead Source Field Slug (optional)', type: 'text', hint: 'Attio people attribute slug used to map the lead source' },
    ],
  },
  odoo: {
    name: 'Odoo',
    description: 'Invoices and subscriptions',
    category: 'ERP',
    tags: ['finance'],
    usage: 'Invoices, subscriptions, and company records for the Finance views and CFO reports.',
    fields: [
      { key: 'url', label: 'Odoo URL', type: 'text' },
      { key: 'database', label: 'Database', type: 'text' },
      { key: 'username', label: 'Username', type: 'text' },
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
  },
  notion: {
    name: 'Notion',
    description: 'Documentation and databases',
    category: 'Productivity',
    tags: ['content', 'sales'],
    usage: 'Playbooks, account briefs, and the content-engine source pages.',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
      { key: 'databaseIds.companies', label: 'Companies Database ID', type: 'text' },
      { key: 'databaseIds.contacts', label: 'Contacts Database ID', type: 'text' },
      { key: 'databaseIds.deals', label: 'Deals Database ID', type: 'text' },
    ],
  },
  sequesto_app: {
    name: 'SEQUESTO App',
    description: 'Our own platform (app.sequesto.com) via its public API',
    category: 'Internal',
    usage: 'Workspaces, Knowledge Hub files and workspace.ready webhooks in our own SEQUESTO organisation.',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'Created by an org admin under Settings → API keys in app.sequesto.com. Expires after at most a year.' },
      { key: 'apiUrl', label: 'API URL (optional)', type: 'text', hint: 'Defaults to https://alb-public.sequesto.com/rest-api/public/v1' },
    ],
  },
  resend: {
    name: 'Resend',
    description: 'Transactional email service',
    category: 'Email',
    hidden: true,
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
      { key: 'fromEmail', label: 'From Email', type: 'email' },
      { key: 'fromName', label: 'From Name', type: 'text' },
    ],
  },
  slack: {
    name: 'Slack',
    description: 'Team notifications',
    category: 'Communication',
    usage: 'Notifications across the OS — closed-won alerts, content review, monitoring, reports.',
    fields: [
      { key: 'botToken', label: 'Bot Token', type: 'password' },
      { key: 'signingSecret', label: 'Signing Secret', type: 'password', hint: 'Found in Slack app settings → Basic Information → App Credentials' },
      { key: 'defaultChannel', label: 'Default Channel (optional)', type: 'text' },
    ],
  },
  slack_james: {
    name: 'Slack (James)',
    description: "James's own Slack bot, for asking James in a DM",
    category: 'Communication',
    usage: 'Receives DMs sent to the James bot and answers them. Separate from the main Slack bot, which keeps notifications and the Organizer to-do capture.',
    fields: [
      { key: 'botToken', label: 'Bot Token', type: 'password', hint: 'api.slack.com/apps → James → OAuth & Permissions → Bot User OAuth Token (xoxb-…)' },
      { key: 'signingSecret', label: 'Signing Secret', type: 'password', hint: 'api.slack.com/apps → James → Basic Information → App Credentials → Signing Secret' },
    ],
  },
  slack_todo: {
    name: 'Slack Todo',
    description: 'Slack todo channel integration',
    category: 'Communication',
    hidden: true,
    fields: [
      { key: 'botToken', label: 'Bot Token', type: 'password' },
      { key: 'todoChannel', label: 'Todo Channel (optional)', type: 'text' },
    ],
  },
  google_analytics: {
    name: 'Google Analytics',
    description: 'Website traffic and analytics',
    category: 'Analytics',
    tags: ['marketing'],
    usage: 'Website traffic for the Marketing dashboard and funnel attribution.',
    fields: [
      { key: 'propertyId', label: 'Property ID', type: 'text', hint: 'GA4 Property ID (e.g. 123456789) — found in Admin > Property Settings' },
      { key: 'serviceAccountKey', label: 'Service Account Key (JSON)', type: 'textarea', hint: 'Paste the entire JSON key file from Google Cloud Console' },
    ],
  },
  customerio: {
    name: 'Customer.io',
    description: 'Email marketing automation',
    category: 'Marketing',
    tags: ['marketing'],
    usage: 'Nurture campaigns and lifecycle messaging for the growth engine.',
    fields: [
      { key: 'region', label: 'Region (us or eu)', type: 'text' },
      { key: 'appApiKey', label: 'App API Key', type: 'password' },
      { key: 'siteId', label: 'Site ID', type: 'text' },
      { key: 'trackApiKey', label: 'Track API Key', type: 'password' },
      { key: 'webhookSigningSecret', label: 'Webhook Signing Secret (optional)', type: 'password', hint: 'Used to verify signed Customer.io webhook payloads.' },
    ],
  },
  lemlist: {
    name: 'lemlist',
    description: 'Multichannel outbound sequences (email + LinkedIn)',
    category: 'Sales',
    tags: ['sales'],
    usage: 'Outbound activity source of truth — campaigns, leads and per-sender activity sync into the OS and drive the Sales -> Outbound dashboard and the weekly outbound digest.',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'lemlist -> Settings -> Integrations -> API & Webhooks -> Generate key. lemlist shows it only once.' },
      { key: 'backfillDays', label: 'First-run backfill (days)', type: 'text', hint: 'How far back to pull activity on the very first sync. Defaults to 90 days.' },
    ],
  },
  google_ads: {
    name: 'Google Ads',
    description: 'Search and display advertising',
    category: 'Advertising',
    tags: ['marketing'],
    usage: 'Spend and campaign metrics for the Marketing dashboard and funnel attribution.',
    fields: [
      { key: 'developerToken', label: 'Developer Token', type: 'password' },
      { key: 'customerId', label: 'Customer ID', type: 'text' },
      { key: 'clientId', label: 'Client ID', type: 'text' },
      { key: 'clientSecret', label: 'Client Secret', type: 'password' },
      { key: 'refreshToken', label: 'Refresh Token', type: 'password' },
      { key: 'loginCustomerId', label: 'Login Customer ID (MCC, optional)', type: 'text' },
    ],
  },
  meta_ads: {
    name: 'Meta Ads',
    description: 'Facebook and Instagram advertising',
    category: 'Advertising',
    tags: ['marketing'],
    usage: 'Spend and campaign metrics for the Marketing dashboard and funnel attribution.',
    fields: [
      { key: 'accessToken', label: 'Access Token', type: 'password' },
      { key: 'adAccountId', label: 'Ad Account ID', type: 'text' },
      { key: 'pixelId', label: 'Pixel / Dataset ID', type: 'text', hint: 'Events Manager dataset the Conversions API sends demo, opportunity and closed-won events to. Required for conversion uploads.' },
      { key: 'pageId', label: 'Facebook Page ID (optional)', type: 'text', hint: 'Page that owns the lead gen forms. Required to sync Meta lead forms into Attio.' },
      { key: 'testEventCode', label: 'Test Event Code (optional)', type: 'text', hint: 'Events Manager → Test events. While set, uploaded events only show in the test view and do not count.' },
      { key: 'customAudienceId', label: 'Custom Audience ID (optional)', type: 'text', hint: 'Audience used for owned-site engagement attribution.' },
    ],
  },
  bing_ads: {
    name: 'Microsoft Ads',
    description: 'Microsoft Advertising (formerly Bing Ads) campaigns',
    category: 'Advertising',
    tags: ['marketing'],
    usage: 'Spend and campaign metrics for the Marketing dashboard and funnel attribution.',
    fields: [
      { key: 'developerToken', label: 'Developer Token', type: 'password', hint: 'Sign in as Super Admin at ads.microsoft.com/cc/Settings/DevSettings (Developer settings) → Request Token' },
      { key: 'customerId', label: 'Customer ID', type: 'text', hint: 'Manager account id: the "cid=" value in the ads.microsoft.com Campaigns page URL' },
      { key: 'accountId', label: 'Account ID', type: 'text', hint: 'Ad account id: the "aid=" value in the same URL (not the 8-character account number)' },
      { key: 'clientId', label: 'Client ID', type: 'text', hint: 'Azure portal → App registrations → your app → Overview → Application (client) ID' },
      { key: 'clientSecret', label: 'Client Secret', type: 'password', hint: 'Same app → Certificates & secrets → New client secret → copy the Value (needed: the server is a Web client)' },
      { key: 'refreshToken', label: 'Refresh Token', type: 'password', hint: 'Filled by the "Connect Microsoft Advertising" button above, or paste one from the token exchange; renewed automatically' },
      { key: 'accessToken', label: 'Access Token (optional)', type: 'password', hint: 'Not needed with a refresh token; a pasted one expires within about an hour' },
    ],
  },
  linkedin_ads: {
    name: 'LinkedIn Ads',
    description: 'LinkedIn sponsored content and ads',
    category: 'Advertising',
    tags: ['marketing'],
    usage: 'Spend and campaign metrics for the Marketing dashboard and funnel attribution.',
    fields: [
      { key: 'accessToken', label: 'Access Token', type: 'password' },
      { key: 'refreshToken', label: 'Refresh Token', type: 'password', hint: 'OAuth refresh token for automatic token renewal' },
      { key: 'adAccountId', label: 'Ad Account ID', type: 'text' },
      { key: 'clientId', label: 'Client ID', type: 'text', hint: 'Auth tab of the LinkedIn developer app that issued the token' },
      { key: 'clientSecret', label: 'Client Secret', type: 'password', hint: 'Renews the token automatically and verifies Lead Sync webhooks' },
    ],
  },
  linkedin_organic: {
    name: 'LinkedIn Organic',
    description: 'LinkedIn personal post metrics via Community Management API',
    category: 'Analytics',
    tags: ['marketing', 'content'],
    usage: 'Personal-post performance metrics for content reporting.',
    fields: [
      { key: 'clientId', label: 'Client ID', type: 'text', hint: 'From your second LinkedIn Developer App (Community Management API)' },
      { key: 'clientSecret', label: 'Client Secret', type: 'password' },
    ],
  },
  fillout: {
    name: 'Fillout',
    description: 'Form submissions and responses',
    category: 'Forms',
    tags: ['sales', 'marketing'],
    usage: 'Demo-booking and weekly kickoff/retro forms — responses feed the demo flow and the Sales → Meetings tab.',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
  },
  anthropic: {
    name: 'Anthropic',
    description: 'Claude AI models and API',
    category: 'AI',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
  },
  auth0: {
    name: 'Auth0',
    description: 'Authentication and login analytics',
    category: 'Authentication',
    fields: [
      { key: 'domain', label: 'Auth0 Domain', type: 'text', hint: 'e.g. your-tenant.auth0.com' },
      { key: 'clientId', label: 'Client ID', type: 'text' },
      { key: 'clientSecret', label: 'Client Secret', type: 'password' },
    ],
  },
  calendly: {
    name: 'Calendly',
    description: 'Meeting scheduling and booking',
    category: 'Scheduling',
    multiInstance: true,
    tags: ['sales'],
    usage: 'Per-rep meeting feeds (one instance per rep) powering the Sales → Meetings tab.',
    fields: [
      { key: 'ownerSlug', label: 'Owner', type: 'text', hint: 'Which rep this Calendly belongs to: patrick, andrew, or nadia' },
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'Personal Access Token from Calendly' },
      { key: 'organizationUri', label: 'Organization URI', type: 'text', hint: 'e.g. https://api.calendly.com/organizations/xxx' },
      { key: 'webhookSigningKey', label: 'Webhook Signing Key (optional)', type: 'password', hint: 'Used to verify Calendly webhook payloads. Found in Calendly Developer settings.' },
    ],
  },
  contabo: {
    name: 'Contabo',
    description: 'VPS runtime used for outbound infrastructure tracking',
    category: 'Infrastructure',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
      { key: 'projectId', label: 'Project ID (optional)', type: 'text' },
      { key: 'runtimeLabel', label: 'Runtime Label (optional)', type: 'text', hint: 'Human-readable label for this runtime.' },
    ],
  },
  gamma: {
    name: 'Gamma',
    description: 'AI-powered presentations, documents, and webpages',
    category: 'Productivity',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'Your Gamma API key' },
    ],
  },
  deepl: {
    name: 'DeepL',
    description: 'AI-powered translation service',
    category: 'Productivity',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'Your DeepL API authentication key' },
    ],
  },
  fathom_walter: {
    name: 'Fathom (Walter)',
    description: "Walter's Fathom account — call recordings, transcripts and action items",
    category: 'Productivity',
    tags: ['sales'],
    usage: 'Personal Organizer — polls Walter\'s recorded calls and proposes to-dos for review.',
    fields: [
      {
        key: 'apiKey',
        label: 'API Key',
        type: 'password',
        hint: 'From Fathom → Settings → API Access. Keys are scoped to the user who creates them.',
      },
      {
        key: 'recordedByEmail',
        label: 'Recorded by (email)',
        type: 'text',
        hint: 'Only ingest meetings recorded by this address. Leave blank for every meeting the key can see.',
      },
    ],
  },
  fathom_patrick: {
    name: 'Fathom (Patrick)',
    description:
      "Patrick's Fathom account — the sales call archive, separate key from Walter's Fathom integration",
    category: 'Productivity',
    tags: ['sales'],
    usage:
      'Sales call history — the archive of recorded demos and discovery calls, including the pre-2026 calls that never reached Attio. Read-only: this key is never polled by the Personal Organizer.',
    fields: [
      {
        key: 'apiKey',
        label: 'API Key',
        type: 'password',
        hint: "From Fathom → Settings → API Access, created by Patrick. Keys are scoped to the user who creates them, so this key only sees Patrick's recordings.",
      },
      {
        key: 'recordedByEmail',
        label: 'Recorded by (email)',
        type: 'text',
        hint: 'Optional. Restrict to meetings recorded by this address. Leave blank for every meeting the key can see.',
      },
    ],
  },
  perplexity: {
    name: 'Perplexity',
    description: 'AI-powered research and data-backed content intelligence',
    category: 'AI',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'Your Perplexity Sonar API key from perplexity.ai/settings/api' },
    ],
  },
  gemini: {
    name: 'Google Gemini',
    description: 'Gemini models with native Google Search grounding for citation tracking',
    category: 'AI',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'API key from aistudio.google.com/app/apikey' },
      { key: 'defaultModel', label: 'Default Model (optional)', type: 'text', hint: 'e.g. gemini-2.5-pro' },
    ],
  },
  sanity: {
    name: 'Sanity',
    description: 'Headless CMS for structured content',
    category: 'CMS',
    tags: ['content'],
    usage: 'Website CMS — the content engine publishes drafts here for review.',
    fields: [
      { key: 'projectId', label: 'Project ID', type: 'text', hint: 'Found in Sanity project settings (manage.sanity.io)' },
      { key: 'dataset', label: 'Dataset', type: 'text', hint: 'e.g. production' },
      { key: 'apiToken', label: 'API Token', type: 'password', hint: 'Token with read (or read+write) access from manage.sanity.io → API' },
      { key: 'apiVersion', label: 'API Version (optional)', type: 'text', hint: 'e.g. 2024-01-01 — defaults to latest' },
      { key: 'useCdn', label: 'Use CDN (optional)', type: 'text', hint: 'Set to "true" for cached reads, "false" for fresh data. Defaults to false.' },
    ],
  },
  prod_db_sync: {
    name: 'Production DB Sync',
    description: 'AWS credentials for syncing production RDS snapshots',
    category: 'Infrastructure',
    fields: [
      { key: 'awsAccessKeyId', label: 'AWS Access Key ID', type: 'text', hint: 'From the db-sync-snapshot-reader IAM user in production' },
      { key: 'awsSecretAccessKey', label: 'AWS Secret Access Key', type: 'password', hint: 'Keep this secure - it provides read access to production snapshots' },
      { key: 'prodRdsIdentifier', label: 'Production RDS Identifier', type: 'text', hint: 'e.g. production-rds-instance' },
      { key: 'prodKmsKeyArn', label: 'Production KMS Key ARN', type: 'text', hint: 'ARN of the KMS key used to encrypt production RDS snapshots' },
      { key: 'prodAwsRegion', label: 'Production AWS Region', type: 'text', hint: 'e.g. eu-central-1 (defaults to current region if empty)' },
    ],
  },
  jina: {
    name: 'Jina AI',
    description: 'AI-powered web reader for clean content extraction',
    category: 'AI',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'Your Jina AI API key from jina.ai/api-dashboard' },
    ],
  },
  firecrawl: {
    name: 'Firecrawl',
    description: 'Page scraper for competitor research — renders JS and gets past bot blocks that defeat plain fetching',
    category: 'AI',
    tags: ['marketing', 'content'],
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'From firecrawl.dev/app/api-keys. The free plan gives 1,000 scrapes a month, which covers a full 13-competitor sweep.' },
    ],
  },
  serpapi: {
    name: 'SerpAPI',
    description: 'Google SERP data for competitive content analysis',
    category: 'AI',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'Your SerpAPI key from serpapi.com/manage-api-key' },
    ],
  },
  serper: {
    name: 'Serper',
    description: 'Google SERP data (cheaper per search than SerpAPI; no AI-overview detection)',
    category: 'AI',
    tags: ['marketing', 'content'],
    usage: 'Live SERP lookups for content research and SEO: ranking URLs, People-Also-Ask and related searches. Preferred over SerpAPI when both are connected, except where AI-overview detection is needed. Also used by the partner-sourcing scripts.',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'Your Serper key from serper.dev/api-key' },
    ],
  },
  openai: {
    name: 'OpenAI',
    description: 'Embeddings for semantic content quality scoring',
    category: 'AI',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'Your OpenAI API key (used only for embeddings)' },
    ],
  },
  openai_os_agent: {
    name: 'OpenAI OS Agent',
    description: 'James, the OS agent, run on the OpenAI Agents API (beta)',
    category: 'AI',
    usage: 'Runs James in the web app and in Slack DMs. Kept apart from the OpenAI embeddings key so agent spend and permissions stay separate.',
    fields: [
      {
        key: 'apiKey',
        label: 'API Key',
        type: 'password',
        hint: 'OpenAI Platform → API keys. A restricted key needs api.agents.read, api.agents.write and api.responses.write. Not the embeddings key under OpenAI.',
      },
      {
        key: 'driveFolderId',
        label: 'Drive folder ID',
        type: 'text',
        hint: 'Where James saves the Sheets and Docs it creates. Share the folder with the OS service account (sequesto-analytics@os-sequesto-1771412357186.iam.gserviceaccount.com) as Content manager; paste the ID from the folder URL.',
      },
      {
        key: 'monthlyBudgetUsd',
        label: 'Monthly budget (USD)',
        type: 'text',
        hint: 'James stops answering for the rest of the month once its estimated spend reaches this. Default 200.',
      },
    ],
  },
  google_search_console: {
    name: 'Google Search Console',
    description: 'Organic search impressions, clicks, CTR, and position per page',
    category: 'Analytics',
    tags: ['marketing', 'content'],
    usage: 'Organic search performance per page for content and SEO reporting.',
    fields: [
      { key: 'siteUrl', label: 'Site URL', type: 'text', hint: 'e.g. https://www.sequesto.com or sc-domain:sequesto.com' },
      { key: 'serviceAccountKey', label: 'Service Account Key (JSON)', type: 'textarea', hint: 'Same service account as GA4 — paste the full JSON key file' },
    ],
  },
  apollo: {
    name: 'Apollo',
    description: 'B2B contact + firmographic enrichment',
    category: 'Enrichment',
    tags: ['sales'],
    usage: 'Contact and company enrichment for outbound — enriched accounts get pushed to Attio.',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'Master API key from apollo.io/settings/integrations/api' },
    ],
  },
  apollo_sdr: {
    name: 'Apollo SDR',
    description: 'Apollo workspace used by the SDR team — separate key from the main Apollo integration',
    category: 'Enrichment',
    tags: ['sales'],
    usage: 'SDR outbound — sequences, contact sourcing, and engagement under the SDR Apollo workspace.',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'Master API key from apollo.io/settings/integrations/api (SDR workspace)' },
    ],
  },
  theorg: {
    name: 'The Org',
    description: 'Org charts, company structures, and people lookup',
    category: 'Enrichment',
    tags: ['sales'],
    usage: 'Org charts to find the right person to contact at target accounts.',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'API key from theorg.com developer portal — sent as X-Api-Key header' },
    ],
  },
  clay: {
    name: 'Clay',
    description: 'GTM database search, enrichment functions and workflows over the Clay Public API',
    category: 'Enrichment',
    tags: ['sales', 'marketing'],
    usage: 'Credentials only for now: nothing in the OS calls Clay yet beyond the connection test, which checks the key and reports the workspace and its remaining credits.',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'Clay → Settings → Account → API keys (beta), or `clay api-keys create`. Sent as the clay-api-key header. Not the clay login session.' },
    ],
  },
  linear: {
    name: 'Linear',
    description: 'Issue tracking and customer requests for product development',
    category: 'Product',
    tags: ['cs'],
    usage: 'Creates Linear customer requests from Attio Requests that reach the "Product Review" stage (daily at 5am).',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'Personal or service API key from Linear → Settings → Security & access → Personal API keys. Sent in the Authorization header (no Bearer prefix).' },
    ],
  },
  rudderstack: {
    name: 'RudderStack',
    description: 'Customer data platform — event streaming and pipeline management',
    category: 'Analytics',
    tags: ['marketing'],
    usage: 'Event ingestion for website attribution and the marketing funnel.',
    fields: [
      { key: 'accessToken', label: 'Personal Access Token', type: 'password', hint: 'PAT from RudderStack → Settings → Personal access tokens. Used for the control-plane API (api.rudderstack.com/v2).' },
      { key: 'webhookSecret', label: 'Webhook Shared Secret (optional)', type: 'password', hint: 'If set, incoming RudderStack webhooks must send this value in an Authorization (Bearer) or x-rudderstack-secret header. Configure it as a custom header on the RudderStack Webhook destination.' },
    ],
  },
  peec: {
    name: 'Peec AI',
    description: 'AI-visibility tracking — brand mentions, share of voice, and source citations across AI engines',
    category: 'Analytics',
    tags: ['marketing', 'content'],
    usage: 'Daily sync of AI-engine visibility (mentions, share of voice, citations) into peec_* tables.',
    fields: [
      { key: 'apiKey', label: 'Personal Access Token', type: 'password', hint: 'MCP token from app.peec.ai → API Keys → Personal Access Tokens (looks like sku-…)' },
      { key: 'projectId', label: 'Project ID (optional)', type: 'text', hint: 'Peec project id (e.g. or_…). Auto-detected when the token has access to a single project.' },
    ],
  },
  semrush: {
    name: 'Semrush',
    description: 'Keyword rank tracking, visibility, and backlink/referring-domain growth for SEO + content',
    category: 'Analytics',
    tags: ['marketing', 'content'],
    usage: 'Keyword rankings and backlink growth for SEO reporting.',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'API key from semrush.com → Subscription info → API units (Guru tier or above)' },
      { key: 'domain', label: 'Tracked Domain', type: 'text', hint: 'Root domain to track, e.g. sequesto.com' },
      { key: 'database', label: 'Default Database (optional)', type: 'text', hint: 'Regional database code, e.g. nl, be, de, fr, uk. Defaults to nl.' },
      { key: 'siteAuditProjectId', label: 'Site Audit Project ID (optional)', type: 'text', hint: 'Semrush project id with Site Audit enabled (from the Projects API). Enables the Tech/SEO Health site-audit panel. SEQUESTO project: 29835350.' },
    ],
  },
  dataforseo: {
    name: 'DataForSEO',
    description: 'Keyword search volumes per market for content-gap and share-of-search analysis',
    category: 'Analytics',
    fields: [
      { key: 'login', label: 'API Login', type: 'text', hint: 'DataForSEO account login (email) from app.dataforseo.com/api-access' },
      { key: 'password', label: 'API Password', type: 'password', hint: 'DataForSEO API password from app.dataforseo.com/api-access' },
    ],
  },
  rb2b: {
    name: 'RB2B',
    description: 'Person-level website visitor identification (name, LinkedIn, work email) + company firmographics by webhook, plus the credit-metered Identity / Enrichment API',
    category: 'Analytics',
    tags: ['marketing', 'sales'],
    usage: 'De-anonymises website visitors to the person level and feeds them into the GTM engine (people, signals, sourcing, ICP-fit-by-page). The visitor feed is push-only: RB2B POSTs each visitor to /api/webhook/rb2b?token=<webhookToken>. The API key unlocks on-demand lookups (IP → company, email/LinkedIn → business profile, personal email, phone); each lookup spends RB2B API credits. Either field can be set without the other.',
    fields: [
      { key: 'webhookToken', label: 'Webhook Secret (you choose)', type: 'password', hint: "You invent this — RB2B does not issue tokens. Pick any secret (16+ chars) and put it in your RB2B webhook URL as ?token=… (RB2B can't send auth headers, so it rides in the URL)." },
      { key: 'apiKey', label: 'API Key (optional)', type: 'password', hint: 'From the RB2B API Partner dashboard (ui.api.rb2b.com). Separate from the RB2B app and billed in its own credits. Test checks it against the free /credits endpoint.' },
    ],
  },
  ted: {
    name: 'TED (Tenders Electronic Daily)',
    description: 'EU public-procurement notices — the award data behind tender sourcing',
    category: 'Enrichment',
    tags: ['sales'],
    usage: 'Optional. The TED search API answers without a key; adding one only raises the rate limit, which matters for the scheduled award ingest that pages through thousands of notices.',
    fields: [
      { key: 'apiKey', label: 'API Key (optional)', type: 'password', hint: 'developer.ted.europa.eu → Manage API Keys. Leave blank to keep calling TED anonymously (works, but throttled sooner).' },
    ],
  },
  theirstack: {
    name: 'TheirStack',
    description: 'Job-postings data — companies hiring bid/proposal/tender roles, a sourcing signal',
    category: 'Enrichment',
    tags: ['sales'],
    usage: 'Sourcing: detect companies hiring for bid/proposal roles and feed them into the sourcing ledger. Billed per job returned.',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'API token from theirstack.com → Settings → API' },
    ],
  },
  g2: {
    name: 'G2',
    description: 'Buyer Intent — companies researching Sequesto on G2 (profile, pricing, category, comparison and alternatives views) as inbound lead signals',
    category: 'Analytics',
    tags: ['marketing', 'sales'],
    usage: 'Buyer-intent signals — companies researching Sequesto on G2 surface as inbound leads.',
    fields: [
      { key: 'apiKey', label: 'API Token', type: 'password', hint: 'Bearer token from my.g2.com/developers → Access Tokens → Generate Token (needs Buyer Intent / market_signals read access)' },
      { key: 'categoryIds', label: 'Category IDs', type: 'text', hint: 'Required. Comma-separated G2 category ids to pull Buyer Intent for (the categories your product is listed in). Find them in my.G2 or via the categories API.' },
      { key: 'productId', label: 'Product ID (optional)', type: 'text', hint: 'Restrict signals to a single G2 product id. Leave blank to sync all products the token can read.' },
    ],
  },
  jungler: {
    name: 'Jungler',
    description: "LinkedIn engagement signals - who reacts to and comments on our posts, competitors' posts and tracked keywords, enriched with title, company domain and work email",
    category: 'Analytics',
    tags: ['sales', 'marketing'],
    usage: 'Social intent - LinkedIn engagers become Signal Ops signals on the linkedin-organic channel and surface on the GTM hot list.',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'app.jungler.ai -> Settings -> Integrations -> create an API key. Sent as X-API-Key.' },
      { key: 'workspaceId', label: 'Workspace ID (optional)', type: 'text', hint: 'Pins the sync to one Jungler workspace. Blank uses the first one the key can read (and logs a warning if there are several).' },
      { key: 'ownProfileSignalIds', label: 'Our own signals (optional)', type: 'textarea', hint: "Comma- or newline-separated Jungler signal ids that watch OUR profiles. Engagement on these scores as linkedin_post_engagement; every other profile signal counts as competitor or influencer engagement, which is worth more." },
      { key: 'signalIds', label: 'Signals to sync (optional)', type: 'textarea', hint: 'Restrict the sync to these Jungler signal ids. Blank syncs every activated signal in the workspace.' },
    ],
  },
  uptime_robot: {
    name: 'UptimeRobot',
    description: 'Real-time website/app downtime & recovery alerts pushed to Slack via web-hook',
    category: 'Monitoring',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'API key from UptimeRobot → My Settings → API Settings (read-only is sufficient). We poll monitor status every 5 minutes.' },
      { key: 'alertChannelId', label: 'Slack Channel ID', type: 'text', hint: 'Channel to post down/up alerts to, e.g. C04NEFVB7QA. Invite the bot to the channel.' },
      { key: 'dmSlackUserIds', label: 'DM Recipients (Slack user IDs)', type: 'textarea', hint: 'Comma- or newline-separated Slack user IDs (e.g. U0ACW52KZR6) to also DM on every alert.' },
    ],
  },
  pagespeed: {
    name: 'PageSpeed Insights',
    description: 'Core Web Vitals (CrUX field data) plus a daily full Lighthouse audit (Performance, Accessibility, Best Practices, SEO) across the sitemap, with a Slack digest + regression DMs',
    category: 'Analytics',
    fields: [
      { key: 'apiKey', label: 'API Key', type: 'password', hint: 'Google Cloud API key restricted to the PageSpeed Insights API. Optional — the API works unauthenticated at low volume, but a key raises the quota (recommended for a full-sitemap crawl).' },
      { key: 'siteUrl', label: 'Site URL', type: 'text', hint: 'Base URL of the site to audit, e.g. https://sequesto.com. Its /sitemap.xml is crawled to discover pages.' },
      { key: 'sitemapUrl', label: 'Sitemap URL (optional)', type: 'text', hint: 'Override the sitemap location if it is not {siteUrl}/sitemap.xml.' },
      { key: 'formFactors', label: 'Form factors (optional)', type: 'text', hint: 'Comma-separated: mobile, desktop. Default mobile (Google is mobile-first). Add desktop to also audit desktop (doubles runtime/quota).' },
      { key: 'maxUrls', label: 'Max pages (optional)', type: 'text', hint: 'Cap on pages audited per run. Default 100. Pages beyond the cap are skipped (logged).' },
      { key: 'alertChannelId', label: 'Slack Channel ID', type: 'text', hint: 'Channel to post the daily Lighthouse digest to, e.g. C04NEFVB7QA. Invite the bot to the channel.' },
      { key: 'dmSlackUserIds', label: 'Regression DM Recipients (Slack user IDs)', type: 'textarea', hint: 'Comma- or newline-separated Slack user IDs (e.g. U0ACW52KZR6) to DM when a page newly regresses.' },
      { key: 'regressionThresholdPoints', label: 'Regression threshold (optional)', type: 'text', hint: 'DM when any category score drops by at least this many points vs the previous capture. Default 10.' },
      { key: 'minScoreFloor', label: 'Score floor (optional)', type: 'text', hint: 'DM when any category score crosses below this floor. Default 50.' },
    ],
  },
} satisfies Record<string, ProviderDef>;

export type ProviderId = keyof typeof _PROVIDERS;

export const PROVIDERS: { [K in ProviderId]: ProviderDef } = _PROVIDERS;

export const PROVIDER_IDS = Object.keys(PROVIDERS) as [ProviderId, ...ProviderId[]];
